import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Stack,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TablePagination from "@mui/material/TablePagination";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import EtiquetasDialog from './EtiquetasDialog';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import placeholderImage from "../../../assets/noimagen.jpg";
import brandLogo from "../../../assets/Logo.png";
import { BRANDING } from "../../../config/branding";
import { getCategorias } from "../../../services/modules/Categorias";
import { getMarcas } from "../../../services/modules/Marcas";
import { useAuth } from "../../../context/AuthContext";
import {
  insertarProducto,
  getProductosPaged,
  eliminarProducto,
  uploadProductoImagen,
} from "../../../services/modules/Productos";
import PageContainer from "../../../components/PageContainer";

const currencyGTQ = new Intl.NumberFormat("es-GT", {
  style: "currency",
  currency: "GTQ",
  minimumFractionDigits: 2,
});

const PDF_THEME = {
  primary: [33, 150, 243],
  primaryDark: [13, 71, 161],
  bg: [246, 248, 252],
  cardBg: [255, 255, 255],
  border: [229, 231, 235],
  muted: [107, 114, 128],
};

const upgradeToHttpsIfNeeded = (value) => {
  const url = String(value || "");
  if (!url) return url;
  if (typeof window === "undefined") return url;
  if (window.location?.protocol !== "https:") return url;
  return url.startsWith("http://") ? `https://${url.slice("http://".length)}` : url;
};

const getUrlBases = (rawInput) => {
  const raw = String(rawInput || "").trim();
  if (!raw) return { origin: "", base: "" };
  const noTrail = raw.replace(/\/$/, "");
  try {
    const url = new URL(noTrail);
    const basePath = String(url.pathname || "").replace(/\/$/, "");
    return {
      origin: url.origin,
      base: `${url.origin}${basePath}`,
    };
  } catch {
    return { origin: noTrail, base: noTrail };
  }
};

const resolveImageUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return upgradeToHttpsIfNeeded(value);
  const { origin: apiOrigin, base: apiBase } = getUrlBases(BRANDING.apiUrl);
  const { origin: uploadOrigin, base: uploadBase } = getUrlBases(BRANDING.uploadUrl || BRANDING.apiUrl);
  const normalized = value.startsWith("/") ? value : `/${value}`;
  const isUpload = normalized.startsWith("/uploads/") || normalized.startsWith("/upload/");
  const outBase = isUpload ? (uploadOrigin || uploadBase) : (apiBase || apiOrigin);
  const out = outBase ? `${outBase}${normalized}` : normalized;
  return upgradeToHttpsIfNeeded(out);
};

const Productos = () => {
  const [formData, setFormData] = useState({
    id: null,
    codigo: "",
    codigo_barras: "",
    descripcion: "",
    id_categoria: "",
    id_marca: "",
    precio_compra: "",
    precio_venta: "",
    fecha_vencimiento: "",
    cantidad_inicial: 0,
    nivel_minimo: 0,
    modelo: "",
    imagen_url: "",
  });

  const resetForm = () =>
    setFormData({
      id: null,
      codigo: "",
      codigo_barras: "",
      descripcion: "",
      id_categoria: "",
      id_marca: "",
      precio_compra: "",
      precio_venta: "",
      fecha_vencimiento: "",
      cantidad_inicial: 0,
      nivel_minimo: 0,
      modelo: "",
      imagen_url: "",
    });

  const [errors, setErrors] = useState({});
  const isRequiredMissing =
    !String(formData.codigo).trim() ||
    !String(formData.descripcion).trim() ||
    !formData.id_categoria;

  const validateForm = () => {
    const e = {};
    if (!formData.codigo.trim()) e.codigo = "Ingresa el codigo del producto.";
    if (!formData.descripcion.trim()) e.descripcion = "Ingresa la descripcion.";
    if (!formData.id_categoria) e.id_categoria = "Selecciona la marca.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const [categorias, setCategorias] = useState([]);
  const [categoriaInput, setCategoriaInput] = useState("");
  const [marcas, setMarcas] = useState([]);
  const [marcaInput, setMarcaInput] = useState("");

  const [productos, setProductos] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingCatalogPdf, setExportingCatalogPdf] = useState(false);

  const [openForm, setOpenForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [productoEtiquetas, setProductoEtiquetas] = useState(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery("(max-width:600px)");

  const fetchProductos = async () => {
    setLoading(true);
    try {
      const data = await getProductosPaged({ q: busqueda, page, pageSize });

      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      setProductos(items);
      setTotal(Number.isFinite(data?.total) ? data.total : items.length);
    } catch (error) {
      console.error("Error al cargar productos:", error);
      setProductos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const normalizeProductosResponse = (data) => {
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchProductosForExport = async () => {
    const exportPageSize = 100;
    const first = await getProductosPaged({ q: busqueda, page: 0, pageSize: exportPageSize });
    const totalRemote = Number.isFinite(first?.total) ? first.total : null;
    const itemsFirst = normalizeProductosResponse(first);

    if (!totalRemote || itemsFirst.length >= totalRemote) return itemsFirst;

    const maxPages = Math.ceil(totalRemote / exportPageSize);
    const all = [...itemsFirst];

    for (let exportPage = 1; exportPage < maxPages; exportPage += 1) {
      const next = await getProductosPaged({ q: busqueda, page: exportPage, pageSize: exportPageSize });
      const nextItems = normalizeProductosResponse(next);
      if (!nextItems.length) break;
      all.push(...nextItems);
      if (all.length >= totalRemote) break;
    }

    return all;
  };

  const getExportRows = (items) =>
    (Array.isArray(items) ? items : []).map((p) => ({
      Codigo: p.codigo ?? "",
      "Codigo barras": p.codigo_barras ?? "",
      Descripcion: p.descripcion ?? "",
      Categoria: p.categoria ?? "",
      Modelo: p.modelo ?? "",
      "Precio compra": Number(p.precio_compra ?? 0),
      "Precio venta": Number(p.precio_venta ?? 0),
      Cantidad: Number(p.cantidad_inicial ?? 0),
      "Nivel minimo": Number(p.nivel_minimo ?? 0),
    }));

  const exportarExcel = async () => {
    if (exportingExcel || loading) return;
    setExportingExcel(true);
    try {
      const items = await fetchProductosForExport();
      if (!items.length) return alert("No hay productos para exportar.");

      const rows = getExportRows(items);
      const sheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Productos");

      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `Productos_${date}.xlsx`);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("No se pudo exportar el Excel.");
    } finally {
      setExportingExcel(false);
    }
  };

  const exportarPdf = async () => {
    if (exportingPdf || loading) return;
    setExportingPdf(true);
    try {
      const items = await fetchProductosForExport();
      if (!items.length) return alert("No hay productos para exportar.");

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });
      doc.setFontSize(14);
      doc.text("Productos", 40, 40);

      const rows = getExportRows(items);
      const head = [Object.keys(rows[0])];
      const body = rows.map((r) => Object.values(r));
      autoTable(doc, {
        head,
        body,
        startY: 60,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [33, 150, 243] },
      });

      const date = new Date().toISOString().slice(0, 10);
      doc.save(`Productos_${date}.pdf`);
    } catch (error) {
      console.error("Error al exportar PDF:", error);
      alert("No se pudo exportar el PDF.");
    } finally {
      setExportingPdf(false);
    }
  };

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const fetchImageDataUrl = async (url) => {
    try {
      const res = await fetch(upgradeToHttpsIfNeeded(url));
      if (!res.ok) return null;
      const blob = await res.blob();
      return await blobToDataUrl(blob);
    } catch {
      return null;
    }
  };

  const toImageFormat = (dataUrl) => {
    const header = String(dataUrl || "").slice(0, 40).toLowerCase();
    if (header.includes("image/png")) return "PNG";
    if (header.includes("image/webp")) return "WEBP";
    if (header.includes("image/gif")) return "GIF";
    return "JPEG";
  };

  const exportarCatalogoPdf = async () => {
    if (exportingCatalogPdf || loading) return;
    setExportingCatalogPdf(true);
    try {
      const allItems = await fetchProductosForExport();
      const items = (Array.isArray(allItems) ? allItems : []).filter((p) => Number(p?.stock || 0) > 0);
      if (!items.length) return alert("No hay productos con existencia para exportar.");

      const grouped = new Map();
      for (const p of items) {
        const key = String(p?.marca_categoria || "Sin categoría").trim() || "Sin categoría";
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(p);
      }

      const categoriasOrdenadas = Array.from(grouped.keys()).sort((a, b) =>
        a.localeCompare(b, "es", { sensitivity: "base" })
      );

      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "A4" });
      const date = new Date().toISOString().slice(0, 10);
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 40;

      const safeLines = (value) =>
        (Array.isArray(value) ? value : [])
          .map((line) => String(line || "").trim())
          .filter(Boolean);

      const companyName = String(BRANDING?.pdf?.name || BRANDING?.companyName || "Catálogo").trim();
      const companyTagline = String(BRANDING?.companyTagline || "").trim();
      const companyPhone = String(BRANDING?.phoneText || "").trim();
      const companyEmail = String(BRANDING?.companyEmail || "").trim();
      const addressLines = safeLines(BRANDING?.pdf?.addressLines);

      const setFont = (style = "normal") => {
        try {
          doc.setFont("helvetica", style);
        } catch {
          doc.setFont("helvetica");
        }
      };

      const drawCover = async ({ logoDataUrl }) => {
        // Fondo con "gradient" por bandas
        const bands = 26;
        for (let i = 0; i < bands; i += 1) {
          const t = i / Math.max(1, bands - 1);
          const r = Math.round(PDF_THEME.primaryDark[0] * (1 - t) + PDF_THEME.primary[0] * t);
          const g = Math.round(PDF_THEME.primaryDark[1] * (1 - t) + PDF_THEME.primary[1] * t);
          const b = Math.round(PDF_THEME.primaryDark[2] * (1 - t) + PDF_THEME.primary[2] * t);
          doc.setFillColor(r, g, b);
          const y = (pageH * 0.62 * i) / bands;
          const h = (pageH * 0.62) / bands + 1;
          doc.rect(0, y, pageW, h, "F");
        }
        doc.setFillColor(PDF_THEME.bg[0], PDF_THEME.bg[1], PDF_THEME.bg[2]);
        doc.rect(0, pageH * 0.62, pageW, pageH * 0.38, "F");

        const topPad = 58;
        const logoMaxW = 160;
        const logoMaxH = 70;
        if (logoDataUrl && typeof logoDataUrl === "string" && logoDataUrl.startsWith("data:image/")) {
          try {
            const props = doc.getImageProperties(logoDataUrl);
            const pxW = Number(props?.width || 0);
            const pxH = Number(props?.height || 0);
            const scale = pxW > 0 && pxH > 0 ? Math.min(logoMaxW / pxW, logoMaxH / pxH) : 1;
            const drawW = pxW > 0 ? pxW * scale : logoMaxW;
            const drawH = pxH > 0 ? pxH * scale : logoMaxH;
            doc.addImage(
              logoDataUrl,
              toImageFormat(logoDataUrl),
              (pageW - drawW) / 2,
              topPad,
              drawW,
              drawH,
              undefined,
              "FAST"
            );
          } catch {
            // ignore
          }
        }

        doc.setTextColor(255, 255, 255);
        setFont("bold");
        doc.setFontSize(30);
        doc.text("Catálogo", pageW / 2, 170, { align: "center" });
        doc.setFontSize(14);
        setFont("normal");
        doc.text("Productos con existencia", pageW / 2, 194, { align: "center" });

        if (companyTagline) {
          doc.setFontSize(11);
          doc.text(companyTagline, pageW / 2, 220, { align: "center" });
        }

        // Tarjeta de info inferior
        const cardW = pageW - margin * 2;
        const cardH = 170;
        const cardX = margin;
        const cardY = pageH - margin - cardH;

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(PDF_THEME.border[0], PDF_THEME.border[1], PDF_THEME.border[2]);
        doc.roundedRect(cardX, cardY, cardW, cardH, 14, 14, "FD");

        doc.setTextColor(17, 24, 39);
        setFont("bold");
        doc.setFontSize(14);
        doc.text(companyName || "Catálogo de productos", cardX + 18, cardY + 34);

        setFont("normal");
        doc.setFontSize(10);
        doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
        doc.text(`Fecha: ${date}`, cardX + 18, cardY + 56);
        doc.text(`Total productos: ${items.length}`, cardX + 18, cardY + 74);
        doc.text(`Categorías: ${categoriasOrdenadas.length}`, cardX + 18, cardY + 92);
        doc.text("Solo incluye productos con stock > 0.", cardX + 18, cardY + 110);

        const rightX = cardX + cardW / 2 + 10;
        const rightY = cardY + 56;
        doc.setTextColor(17, 24, 39);
        setFont("bold");
        doc.text("Contacto", rightX, rightY);

        setFont("normal");
        doc.setFontSize(10);
        doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
        let infoY = rightY + 18;
        if (companyPhone) {
          doc.text(companyPhone, rightX, infoY);
          infoY += 16;
        }
        if (companyEmail) {
          doc.text(companyEmail, rightX, infoY);
          infoY += 16;
        }
        if (addressLines.length) {
          const addr = addressLines.slice(0, 3);
          doc.text(addr, rightX, infoY);
        }

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        setFont("normal");
        doc.text("Generado desde el sistema", pageW / 2, pageH * 0.62 - 18, { align: "center" });

        doc.setTextColor(17, 24, 39);
      };

      const drawToc = ({ plan }) => {
        const linesPerPage = 30;
        const tocPages = Math.max(1, Math.ceil(plan.length / linesPerPage));

        const startPages = new Map();
        let current = 2 + tocPages; // cover=1, toc=[2..(1+tocPages)], categorías comienzan en (2+tocPages)
        plan.forEach((entry) => {
          startPages.set(entry.cat, current);
          current += entry.pagesCount;
        });

        for (let p = 0; p < tocPages; p += 1) {
          doc.addPage();
          doc.setFillColor(255, 255, 255);
          doc.rect(0, 0, pageW, pageH, "F");

          doc.setFillColor(PDF_THEME.primary[0], PDF_THEME.primary[1], PDF_THEME.primary[2]);
          doc.rect(0, 0, pageW, 78, "F");
          doc.setTextColor(255, 255, 255);
          setFont("bold");
          doc.setFontSize(18);
          doc.text("Índice", margin, 50);
          doc.setFontSize(10);
          setFont("normal");
          doc.text(companyName, pageW - margin, 50, { align: "right" });

          doc.setTextColor(17, 24, 39);
          setFont("normal");
          doc.setFontSize(11);

          const startIdx = p * linesPerPage;
          const slice = plan.slice(startIdx, startIdx + linesPerPage);
          let y = 110;
          slice.forEach((entry) => {
            const pageNum = startPages.get(entry.cat);
            const left = String(entry.cat || "");
            const right = String(pageNum || "");

            doc.text(left, margin, y);
            doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
            doc.text(right, pageW - margin, y, { align: "right" });
            doc.setTextColor(17, 24, 39);
            y += 18;
          });

          // Footer simple
          doc.setDrawColor(PDF_THEME.border[0], PDF_THEME.border[1], PDF_THEME.border[2]);
          doc.line(margin, pageH - 46, pageW - margin, pageH - 46);
          doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
          doc.setFontSize(9);
          doc.text(`Fecha: ${date}`, margin, pageH - 28);
          doc.text(`Página ${doc.getNumberOfPages()}`, pageW - margin, pageH - 28, { align: "right" });
        }
      };

      const drawHeader = ({ cat, idx, total }) => {
        const headerH = 78;
        doc.setFillColor(PDF_THEME.primary[0], PDF_THEME.primary[1], PDF_THEME.primary[2]);
        doc.rect(0, 0, pageW, headerH, "F");

        doc.setTextColor(255, 255, 255);
        setFont("bold");
        doc.setFontSize(10);
        doc.text(companyName, margin, 30);

        setFont("bold");
        doc.setFontSize(18);
        doc.text(String(cat || ""), margin, 56);

        setFont("normal");
        doc.setFontSize(10);
        doc.text(`${idx}/${total}`, pageW - margin, 56, { align: "right" });

        doc.setTextColor(17, 24, 39);
        return headerH;
      };

      const drawFooter = ({ pageLabel }) => {
        doc.setDrawColor(PDF_THEME.border[0], PDF_THEME.border[1], PDF_THEME.border[2]);
        doc.line(margin, pageH - 46, pageW - margin, pageH - 46);

        doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
        setFont("normal");
        doc.setFontSize(9);
        const leftParts = [companyPhone, companyEmail].filter(Boolean).slice(0, 2);
        const leftText = leftParts.join(" · ") || companyName;
        doc.text(leftText, margin, pageH - 28);
        doc.text(String(pageLabel || ""), pageW - margin, pageH - 28, { align: "right" });

        doc.setTextColor(17, 24, 39);
      };

      const imageCache = new Map();

      const chunk = (arr, size) => {
        const out = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };

      const drawCard = ({ x, y, w, h, img, marca, nombre, meta, precio }) => {
        const padding = 12;
        const radius = 14;

        // Shadow
        doc.setFillColor(PDF_THEME.border[0], PDF_THEME.border[1], PDF_THEME.border[2]);
        doc.roundedRect(x + 2, y + 3, w, h, radius, radius, "F");

        // Card
        doc.setFillColor(PDF_THEME.cardBg[0], PDF_THEME.cardBg[1], PDF_THEME.cardBg[2]);
        doc.setDrawColor(PDF_THEME.border[0], PDF_THEME.border[1], PDF_THEME.border[2]);
        doc.roundedRect(x, y, w, h, radius, radius, "FD");

        const imgX = x + padding;
        const imgY = y + padding;
        const imgW = w - padding * 2;
        const imgH = Math.max(60, h * 0.56);

        // Price pill
        const priceText = String(precio || "");
        setFont("bold");
        doc.setFontSize(10);
        const priceW = Math.min(imgW, doc.getTextWidth(priceText) + 18);
        const pillH = 18;
        const pillX = x + w - padding - priceW;
        const pillY = y + padding;
        doc.setFillColor(PDF_THEME.primary[0], PDF_THEME.primary[1], PDF_THEME.primary[2]);
        doc.roundedRect(pillX, pillY, priceW, pillH, 9, 9, "F");
        doc.setTextColor(255, 255, 255);
        doc.text(priceText, pillX + priceW / 2, pillY + 12, { align: "center", baseline: "middle" });
        doc.setTextColor(17, 24, 39);

        if (img && typeof img === "string" && img.startsWith("data:image/")) {
          try {
            const props = doc.getImageProperties(img);
            const pxW = Number(props?.width || 0);
            const pxH = Number(props?.height || 0);
            const fitScale = pxW > 0 && pxH > 0 ? Math.min(imgW / pxW, imgH / pxH) : 1;
            const scale = Math.min(1, fitScale); // no agrandar, solo encajar
            const drawW = pxW > 0 ? pxW * scale : imgW;
            const drawH = pxH > 0 ? pxH * scale : imgH;
            const dx = imgX + (imgW - drawW) / 2;
            const dy = imgY + (imgH - drawH) / 2;
            doc.addImage(img, toImageFormat(img), dx, dy, drawW, drawH, undefined, "FAST");
          } catch {
            doc.setDrawColor(PDF_THEME.border[0], PDF_THEME.border[1], PDF_THEME.border[2]);
            doc.roundedRect(imgX, imgY, imgW, imgH, 10, 10);
            doc.setFontSize(8);
            doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
            setFont("normal");
            doc.text("Sin imagen", imgX + 10, imgY + imgH / 2);
            doc.setTextColor(17, 24, 39);
          }
        } else {
          doc.setDrawColor(PDF_THEME.border[0], PDF_THEME.border[1], PDF_THEME.border[2]);
          doc.roundedRect(imgX, imgY, imgW, imgH, 10, 10);
          doc.setFontSize(8);
          doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
          setFont("normal");
          doc.text("Sin imagen", imgX + 10, imgY + imgH / 2);
          doc.setTextColor(17, 24, 39);
        }

        const textTop = imgY + imgH + 14;
        const maxTextW = imgW;

        // Marca
        if (marca) {
          doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
          setFont("bold");
          doc.setFontSize(8);
          doc.text(String(marca).toUpperCase().slice(0, 28), imgX, textTop);
          doc.setTextColor(17, 24, 39);
        }

        // Nombre (2 líneas)
        setFont("bold");
        doc.setFontSize(10);
        const nameLines = doc.splitTextToSize(String(nombre || ""), maxTextW);
        const finalNameLines = nameLines.slice(0, 2);
        doc.text(finalNameLines, imgX, textTop + 14, { baseline: "top" });

        // Meta (modelo/código) en 1 línea
        if (meta) {
          doc.setTextColor(PDF_THEME.muted[0], PDF_THEME.muted[1], PDF_THEME.muted[2]);
          setFont("normal");
          doc.setFontSize(8);
          const metaLine = doc.splitTextToSize(String(meta || ""), maxTextW)[0] || "";
          // Deja un poco más de aire respecto al nombre
          doc.text(metaLine, imgX, y + h - 8, { baseline: "bottom" });
          doc.setTextColor(17, 24, 39);
        }
      };

      // Portada + índice
      let logoDataUrl = null;
      try {
        logoDataUrl = await fetchImageDataUrl(brandLogo);
      } catch {
        logoDataUrl = null;
      }
      await drawCover({ logoDataUrl });

      const tocPlan = categoriasOrdenadas.map((cat) => {
        const productosCat = grouped.get(cat) || [];
        return { cat, pagesCount: Math.max(1, Math.ceil(productosCat.length / 8)) };
      });
      drawToc({ plan: tocPlan });

      for (const cat of categoriasOrdenadas) {
        const productosCat = grouped.get(cat) || [];
        productosCat.sort((a, b) =>
          String(a?.descripcion || "").localeCompare(String(b?.descripcion || ""), "es", {
            sensitivity: "base",
          })
        );

        // Precargar imágenes de esta categoría (cachea por URL)
        for (const p of productosCat) {
          const url = resolveImageUrl(p?.imagen_url) || "";
          if (!url) continue;
          if (imageCache.has(url)) continue;
          // eslint-disable-next-line no-await-in-loop
          const dataUrl = await fetchImageDataUrl(url);
          imageCache.set(url, dataUrl);
        }

        const pages = chunk(productosCat, 8);
        const topHeaderH = 78;
        const footerGap = 64;
        const topY = topHeaderH + 28;
        const colGap = 16;
        const rowGap = 16;
        const cols = 2;
        const rows = 4;
        const cardW = (pageW - margin * 2 - colGap * (cols - 1)) / cols;
        const availableH = pageH - topY - footerGap - rowGap * (rows - 1);
        const cardH = availableH / rows;

        pages.forEach((pageItems, idx) => {
          doc.addPage();
          doc.setFillColor(PDF_THEME.bg[0], PDF_THEME.bg[1], PDF_THEME.bg[2]);
          doc.rect(0, 0, pageW, pageH, "F");
          drawHeader({ cat, idx: idx + 1, total: pages.length });
          drawFooter({ pageLabel: `Página ${doc.getNumberOfPages()}` });

          pageItems.forEach((p, i) => {
            const r = Math.floor(i / cols);
            const c = i % cols;
            const x = margin + c * (cardW + colGap);
            const y = topY + r * (cardH + rowGap);

            const url = resolveImageUrl(p?.imagen_url) || "";
            const img = url ? imageCache.get(url) : null;
            const metaParts = [];
            if (p?.modelo) metaParts.push(`Modelo: ${p.modelo}`);
            if (p?.codigo) metaParts.push(`Código: ${p.codigo}`);

            drawCard({
              x,
              y,
              w: cardW,
              h: cardH,
              img,
              marca: p?.marca_categoria || "",
              nombre: p?.descripcion || "",
              meta: metaParts.join(" · "),
              precio: currencyGTQ.format(Number(p?.precio_venta || 0)),
            });
          });
        });
      }

      doc.save(`Catalogo_Productos_${date}.pdf`);
    } catch (error) {
      console.error("Error al exportar catálogo PDF:", error);
      alert("No se pudo exportar el catálogo PDF.");
    } finally {
      setExportingCatalogPdf(false);
    }
  };

  useEffect(() => {
    const cargarCategorias = async () => {
      try {
        const data = await getCategorias();
        setCategorias(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error al cargar categorias:", error);
      }
    };
    cargarCategorias();

    const cargarMarcas = async () => {
      try {
        const data = await getMarcas({ activos: "1" });
        setMarcas(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error al cargar marcas (categorías):", error);
        setMarcas([]);
      }
    };
    cargarMarcas();

    fetchProductos();
  }, []);

  useEffect(() => {
    const id = setTimeout(fetchProductos, 300);
    return () => clearTimeout(id);
  }, [busqueda, page, pageSize]);

  useEffect(() => {
    setPage(0);
  }, [busqueda]);

  const handleOpenNuevo = () => {
    resetForm();
    setErrors({});
    setCategoriaInput("");
    setMarcaInput("");
    setPreviewSrc("");
    setOpenForm(true);
  };

  const handleEditar = (producto) => {
    setFormData({
      id: producto.id,
      codigo: producto.codigo ?? "",
      codigo_barras: producto.codigo_barras ?? "",
      descripcion: producto.descripcion ?? "",
      id_categoria: producto.id_categoria ?? "",
      id_marca: producto.id_marca ?? "",
      precio_compra: producto.precio_compra ?? "",
      precio_venta: producto.precio_venta ?? "",
      modelo: producto.modelo ?? "",
      cantidad_inicial: producto.cantidad_inicial ?? 0,
      nivel_minimo: producto.nivel_minimo ?? 0,
      imagen_url: producto.imagen_url ?? "",
    });

    const cat = categorias.find((c) => String(c.id_categoria) === String(producto.id_categoria));
    setCategoriaInput(cat?.nombre || "");

    const marca = marcas.find((m) => String(m.id_marca) === String(producto.id_marca));
    setMarcaInput(marca?.nombre || "");

    const imageSrc = resolveImageUrl(producto.imagen_url);
    setPreviewSrc(imageSrc);

    setErrors({});
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    if (saving) return;
    setOpenForm(false);
    resetForm();
    setErrors({});
    setCategoriaInput("");
    setMarcaInput("");
    setPreviewSrc("");
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);

      const data = await uploadProductoImagen(file);
      let newPath = data.path || data.url || "";
      if (!data.path && data.url) {
        try {
          newPath = new URL(String(data.url)).pathname || newPath;
        } catch {
          // Mantener como venga si no se puede parsear
        }
      }

      setFormData((prev) => ({
        ...prev,
        imagen_url: newPath || prev.imagen_url,
      }));

      setPreviewSrc(resolveImageUrl(newPath));
    } catch (error) {
      console.error("Error al subir imagen:", error);
      alert("No se pudo subir la imagen (max. 8MB).");
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleGuardarProducto = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const producto = {
        ...formData,
        codigo: formData.codigo.trim(),
        codigo_barras: String(formData.codigo_barras || "").trim(),
        descripcion: formData.descripcion.trim(),
        cantidad_inicial: formData.cantidad_inicial || 0,
        nivel_minimo: formData.nivel_minimo || 0,
        imagen_url: formData.imagen_url.trim(),
      };

      const res = await insertarProducto(producto);

      alert(res.message);
      await fetchProductos();
      handleCloseForm();
    } catch (error) {
      console.error(error);
      alert("Error al guardar el producto");
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarProducto = async (producto) => {
    const confirmed = window.confirm(
      `Deseas eliminar este producto?\n\n${producto?.codigo || ""} - ${producto?.descripcion || ""}`.trim()
    );
    if (!confirmed) return;
    try {
      setSaving(true);
      await eliminarProducto(producto.id);
      await fetchProductos();
    } catch (error) {
      console.error("Error al eliminar producto:", error);
      alert("No se pudo eliminar el producto.");
    } finally {
      setSaving(false);
    }
  };

  const rows = Array.isArray(productos) ? productos : [];

  const summaryText = useMemo(() => {
    const base = `Mostrando ${rows.length} de ${total}`;
    return busqueda ? `${base} resultados para "${busqueda}"` : `${base} productos`;
  }, [rows.length, total, busqueda]);

  const tableHeadSx = useMemo(
    () => ({
      backgroundColor:
        theme.palette.mode === "light" ? "rgba(15,23,42,0.04)" : "rgba(255,255,255,0.04)",
    }),
    [theme.palette.mode]
  );

  const headCellSx = {
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: 13,
    borderBottom: "none",
  };

  const emptyState = !loading && rows.length === 0;

  return (
    <>
      <PageContainer>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: 0,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                bgcolor: tableHeadSx.backgroundColor,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Productos registrados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summaryText}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                {isAdmin && (
                  <Button variant="contained" color="success" onClick={handleOpenNuevo}>
                    Registrar Producto
                  </Button>
                )}

                <Tooltip title="Exportar PDF">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={exportarPdf}
                      disabled={loading || exportingPdf || exportingExcel || exportingCatalogPdf || !productos.length}
                    >
                      PDF
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Descargar catálogo (solo stock)">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<MenuBookIcon />}
                      onClick={exportarCatalogoPdf}
                      disabled={loading || exportingCatalogPdf || exportingExcel || exportingPdf}
                    >
                      Catálogo
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Exportar Excel">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportarExcel}
                      disabled={loading || exportingExcel || exportingPdf || exportingCatalogPdf || !productos.length}
                    >
                      Excel
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Box>

            <Divider />

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              sx={{ px: { xs: 2, md: 3 }, py: 2 }}
            >
              <TextField
                size="small"
                placeholder="Buscar por codigo o descripcion..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    fetchProductos();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar ahora">
                        <IconButton onClick={fetchProductos}>
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>

                      {!!busqueda && (
                        <Tooltip title="Limpiar busqueda">
                          <IconButton onClick={() => setBusqueda("")}>
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280, flex: 1 }}
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setBusqueda("")}>
                  Limpiar
                </Button>
                {isAdmin && (
                  <Button variant="contained" onClick={handleOpenNuevo}>
                    Nuevo producto
                  </Button>
                )}
              </Stack>
            </Stack>

            {loading && <LinearProgress />}

            {isXs ? (
              <Box px={{ xs: 2, md: 3 }} pb={2}>
                {loading && (
                  <Stack spacing={1.5}>
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <Paper key={idx} sx={{ p: 2, borderRadius: 2, boxShadow: 0 }}>
                        <Skeleton variant="rounded" height={200} />
                      </Paper>
                    ))}
                  </Stack>
                )}

                {!loading &&
                  rows.map((prod) => {
                    const imageSrc = resolveImageUrl(prod.imagen_url) || placeholderImage;
                    const precio = Number(prod.precio_venta) || 0;

                    return (
                      <Paper
                        key={prod.id}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          component="img"
                          src={imageSrc}
                          sx={{
                            width: "100%",
                            height: 180,
                            borderRadius: 2,
                            objectFit: "cover",
                            mb: 1.5,
                            cursor: "pointer",
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = placeholderImage;
                          }}
                          onClick={() => {
                            setPreviewSrc(imageSrc);
                            setPreviewOpen(true);
                          }}
                        />

                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {prod.descripcion}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Codigo: {prod.codigo}
                              {prod.codigo_barras ? ` • Barras: ${prod.codigo_barras}` : ""}
                              {" • "}
                              Marca: {prod.categoria}
                            </Typography>
                            {isAdmin && (
                              <Typography variant="body2">
                                Compra: {currencyGTQ.format(prod.precio_compra || 0)}
                              </Typography>
                            )}
                          </Box>

                          {isAdmin && (
                            <Stack direction="row">
                              <Tooltip title="Imprimir etiquetas"><IconButton aria-label="Imprimir etiquetas" color="primary" onClick={() => setProductoEtiquetas(prod)}><LocalPrintshopIcon /></IconButton></Tooltip>
                              <IconButton color="primary" onClick={() => handleEditar(prod)}>
                                <EditIcon />
                              </IconButton>
                              <IconButton color="error" onClick={() => handleEliminarProducto(prod)}>
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          )}
                        </Stack>

                        <Divider sx={{ my: 1 }} />

                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" fontWeight={600}>
                            Precio: {currencyGTQ.format(precio)}
                          </Typography>
                          <Typography variant="body2">
                            Stock: <strong>{prod.cantidad_inicial}</strong>
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}

                {emptyState && (
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: "center",
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle1">Sin productos con ese criterio</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ajusta la busqueda o registra un nuevo producto.
                    </Typography>
                  </Paper>
                )}
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 560, overflow: "auto" }}>
                <Table stickyHeader size="small">
                  <TableHead sx={tableHeadSx}>
                    <TableRow>
                      <TableCell sx={headCellSx}>Imagen</TableCell>
                      <TableCell sx={headCellSx}>Codigo</TableCell>
                      <TableCell sx={headCellSx}>Cod. Barras</TableCell>
                      <TableCell sx={headCellSx}>Descripcion</TableCell>
                      <TableCell sx={headCellSx}>Marca</TableCell>
                      {isAdmin && (
                        <TableCell sx={headCellSx} align="right">
                          Precio Compra
                        </TableCell>
                      )}
                      <TableCell sx={headCellSx} align="right">
                        Precio Venta
                      </TableCell>
                      <TableCell sx={headCellSx} align="right">
                        Stock
                      </TableCell>
                      {isAdmin && <TableCell sx={headCellSx}>Acciones</TableCell>}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading &&
                      Array.from({ length: 5 }).map((_, idx) => (
                        <TableRow key={idx}>
                          <TableCell colSpan={isAdmin ? 9 : 7}>
                            <Skeleton variant="rounded" height={56} />
                          </TableCell>
                        </TableRow>
                      ))}

                    {!loading &&
                      rows.map((prod) => {
                        const imageSrc = resolveImageUrl(prod.imagen_url) || placeholderImage;
                        return (
                          <TableRow
                            key={prod.id}
                            hover
                            sx={{
                              "& td": { borderBottom: "1px solid", borderColor: "divider", py: 1.5 },
                              "&:last-of-type td": { borderBottom: 0 },
                            }}
                          >
                            <TableCell>
                              <Box
                                component="img"
                                src={imageSrc}
                                sx={{
                                  width: 72,
                                  height: 72,
                                  borderRadius: 2,
                                  objectFit: "cover",
                                  cursor: "pointer",
                                }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = placeholderImage;
                                }}
                                onClick={() => {
                                  setPreviewSrc(imageSrc);
                                  setPreviewOpen(true);
                                }}
                              />
                            </TableCell>

                            <TableCell>{prod.codigo}</TableCell>
                            <TableCell>{prod.codigo_barras || "-"}</TableCell>
                            <TableCell>{prod.descripcion}</TableCell>
                            <TableCell>{prod.categoria}</TableCell>

                            {isAdmin && (
                              <TableCell align="right">
                                {currencyGTQ.format(prod.precio_compra || 0)}
                              </TableCell>
                            )}

                            <TableCell align="right">
                              {currencyGTQ.format(prod.precio_venta || 0)}
                            </TableCell>
                            <TableCell align="right">{prod.cantidad_inicial}</TableCell>

                            {isAdmin && (
                              <TableCell>
                                <Stack direction="row" spacing={0.5}>
                                  <Tooltip title="Imprimir etiquetas"><IconButton aria-label="Imprimir etiquetas" color="primary" onClick={() => setProductoEtiquetas(prod)}><LocalPrintshopIcon /></IconButton></Tooltip>
                                  <Tooltip title="Editar">
                                    <IconButton color="primary" onClick={() => handleEditar(prod)}>
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Eliminar">
                                    <IconButton color="error" onClick={() => handleEliminarProducto(prod)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}

                    {emptyState && (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 9 : 7}>
                          <Box textAlign="center" py={3}>
                            <Typography variant="subtitle1">Sin productos con ese criterio</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Ajusta la busqueda o registra uno nuevo.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider />

            <Box px={{ xs: 2, md: 3 }} py={1}>
              <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={pageSize}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 20, 50]}
              />
            </Box>
          </Paper>
        </Stack>
      </PageContainer>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            p: 2,
            borderRadius: 2,
            boxShadow: 24,
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        >
          <Box
            component="img"
            src={previewSrc || placeholderImage}
            alt="Vista previa"
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = placeholderImage;
            }}
          />
        </Box>
      </Modal>

      {productoEtiquetas && <EtiquetasDialog key={productoEtiquetas.id} producto={productoEtiquetas} onClose={() => { setProductoEtiquetas(null); fetchProductos(); }} />}
      <Dialog
        open={openForm}
        onClose={saving ? undefined : handleCloseForm}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreen}
      >
        <DialogTitle>{formData.id ? "Editar Producto" : "Registrar Producto"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Codigo"
                value={formData.codigo}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, codigo: e.target.value }));
                  if (errors.codigo) setErrors((er) => ({ ...er, codigo: undefined }));
                }}
                error={!!errors.codigo}
                helperText={errors.codigo}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Codigo de barras"
                value={formData.codigo_barras}
                onChange={(e) => setFormData((p) => ({ ...p, codigo_barras: e.target.value }))}
                helperText="Opcional"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Descripcion"
                value={formData.descripcion}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, descripcion: e.target.value }));
                  if (errors.descripcion) setErrors((er) => ({ ...er, descripcion: undefined }));
                }}
                error={!!errors.descripcion}
                helperText={errors.descripcion}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={categorias}
                value={
                  categorias.find((cat) => String(cat.id_categoria) === String(formData.id_categoria)) ||
                  null
                }
                inputValue={categoriaInput}
                onInputChange={(_, v) => setCategoriaInput(v)}
                onChange={(_, newValue) => {
                  setFormData((p) => ({
                    ...p,
                    id_categoria: newValue?.id_categoria || "",
                  }));
                  if (errors.id_categoria) setErrors((er) => ({ ...er, id_categoria: undefined }));
                  setCategoriaInput(newValue?.nombre || "");
                }}
                getOptionLabel={(option) => option?.nombre || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Marca"
                    error={!!errors.id_categoria}
                    helperText={errors.id_categoria}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={marcas}
                value={marcas.find((m) => String(m.id_marca) === String(formData.id_marca)) || null}
                inputValue={marcaInput}
                onInputChange={(_, v) => setMarcaInput(v)}
                onChange={(_, newValue) => {
                  setFormData((p) => ({
                    ...p,
                    id_marca: newValue?.id_marca || "",
                  }));
                  setMarcaInput(newValue?.nombre || "");
                }}
                getOptionLabel={(option) => option?.nombre || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categoría"
                    helperText="Opcional"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Precio Compra"
                type="number"
                value={formData.precio_compra}
                onChange={(e) => setFormData((p) => ({ ...p, precio_compra: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Precio Venta"
                type="number"
                value={formData.precio_venta}
                onChange={(e) => setFormData((p) => ({ ...p, precio_venta: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Modelo"
                value={formData.modelo}
                onChange={(e) => setFormData((p) => ({ ...p, modelo: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Cantidad Inicial"
                type="number"
                value={formData.cantidad_inicial}
                onChange={(e) => setFormData((p) => ({ ...p, cantidad_inicial: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Nivel Minimo"
                type="number"
                value={formData.nivel_minimo}
                onChange={(e) => setFormData((p) => ({ ...p, nivel_minimo: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <TextField
                  label="Imagen (URL opcional)"
                  value={formData.imagen_url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, imagen_url: e.target.value }))}
                  fullWidth
                />

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button variant="outlined" component="label" disabled={uploadingImage}>
                    {uploadingImage ? "Subiendo..." : "Seleccionar imagen"}
                    <input type="file" accept="image/*" hidden onChange={handleUploadImage} />
                  </Button>

                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CameraAltIcon />}
                    disabled={uploadingImage}
                    sx={{ borderRadius: 999 }}
                  >
                    {uploadingImage ? "Subiendo..." : "Tomar foto"}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      hidden
                      onChange={handleUploadImage}
                    />
                  </Button>
                </Stack>
              </Stack>

              {formData.imagen_url && (
                <Box
                  component="img"
                  src={resolveImageUrl(formData.imagen_url) || placeholderImage}
                  alt="Vista previa"
                  sx={{
                    mt: 2,
                    width: 240,
                    height: 180,
                    borderRadius: 2,
                    objectFit: "cover",
                    border: "1px solid #ddd",
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = placeholderImage;
                  }}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseForm} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleGuardarProducto}
            disabled={saving || isRequiredMissing}
          >
            {formData.id ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Productos;
